/**
 * API Service for Candidate Onboarding Flow
 * Integrates with candidate-service session-based resume processing APIs
 */

import { ENV } from '../../../config/env';
import { AUTH_STORAGE_KEYS } from '../../auth/constants';

const API_BASE_URL = ENV.CANDIDATE_SERVICE_URL;

// Helper to validate API URL before making requests
const getApiUrl = (endpoint: string): string => {
  if (!API_BASE_URL) {
    throw new Error('VITE_CANDIDATE_SERVICE_URL is not configured. Please set it in your environment variables.');
  }
  return `${API_BASE_URL}${endpoint}`;
};

// Helper to get auth token from storage
const getAuthToken = (): string | null => {
  return localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN);
};

// Helper to get authorization headers
const getAuthHeaders = (): Record<string, string> => {
  const token = getAuthToken();
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

export interface SessionResponse<T = any> {
  success: boolean;
  message: string;
  error?: string;
  payload?: T;
}

export interface SessionData {
  session_id: string;
  user_id: string;
  email: string;
  status: 'uploaded' | 'parsed' | 'collecting_info' | 'completed' | 'error';
  resume_file_info?: {
    original_filename: string;
    file_size: number;
    file_type: string;
  };
  extracted_text?: string;
  llm_parsed_data?: any;
  missing_content_questions?: string[];
  remaining_questions?: QuestionInfo[];
  collected_answers?: Record<string, string>;
  candidate_id?: string;
  created_at: string;
  updated_at: string;
  expires_at: string;
}

export interface QuestionInfo {
  question_id: number;
  question: string;
  status: 'pending' | 'answered' | 'skipped';
  answer?: string;
}

export interface CandidateData {
  id: string;
  _id?: string; // MongoDB ID (legacy)
  applicationId?: string;
  supabaseUserId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  linkedInUrl?: string;
  resumeUrl?: string;
  resumeS3Key?: string;
  resumePending?: boolean;
  resumeStatus?: string;
  parsedResume?: ParsedResume;
  profileCompleted?: boolean;
  preferences?: any; // Career goals and preferences
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  approvedAt?: string;
}

export interface Certification {
  name: string;
  issuer?: string;
  issueDate?: string;
  expirationDate?: string;
  credentialId?: string;
  url?: string;
  year?: string; // Backend may return year instead of issueDate
}

export interface ParsedResume {
  rawText?: string;
  skills: string[];
  experience: WorkExperience[];
  education: Education[];
  certifications?: Certification[];
  summary: string;
  totalYearsExp: number;
  parsedAt: string;
  parserVersion: string;
  confidenceScore: number;
  rawJson?: any;
}

export interface WorkExperience {
  title: string;
  company: string;
  startDate: string;
  endDate: string;
  description: string;
  isCurrent?: boolean;
  is_current?: boolean; // Backend uses both
  responsibilities?: string[];
  location?: string;
}

export interface Education {
  degree: string;
  institution: string;
  fieldOfStudy?: string;
  field_of_study?: string; // Backend may use snake_case
  startDate?: string;
  endDate?: string;
  endYear?: string; // Backend uses this
  gpa?: string;
}

/**
 * Upload resume and start processing session
 */
export async function uploadResume(
  file: File,
  userId: string,
  email: string,
  forceNew: boolean = false
): Promise<SessionResponse<SessionData>> {
  const formData = new FormData();
  formData.append('resume', file);
  formData.append('candidate_id', userId);
  formData.append('email', email);
  if (forceNew) {
    formData.append('force_new', 'true');
  }

  const response = await fetch(getApiUrl('/onboarding/v1/session/upload'), {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to upload resume');
  }

  return response.json();
}

/**
 * Get session details by ID
 */
export async function getSession(sessionId: string): Promise<SessionResponse<SessionData>> {
  const response = await fetch(getApiUrl(`/onboarding/v1/session/${sessionId}`), {
    method: 'GET',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to get session');
  }

  return response.json();
}

/**
 * Answer a question in the session
 */
export async function answerQuestion(
  sessionId: string,
  questionId: number,
  answer: string
): Promise<SessionResponse<SessionData>> {
  const response = await fetch(getApiUrl(`/onboarding/v1/session/${sessionId}/answer`), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      question_id: questionId,
      answer: answer,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to answer question');
  }

  return response.json();
}

/**
 * Skip a question in the session
 */
export async function skipQuestion(
  sessionId: string,
  questionId: number
): Promise<SessionResponse<SessionData>> {
  const response = await fetch(getApiUrl(`/onboarding/v1/session/${sessionId}/skip`), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      question_id: questionId,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to skip question');
  }

  return response.json();
}

/**
 * Complete the session and create/update candidate profile
 */
export async function completeSession(sessionId: string): Promise<SessionResponse<{
  candidate_id: string;
  session_completed: boolean;
}>> {
  const response = await fetch(getApiUrl(`/onboarding/v1/session/${sessionId}/complete`), {
    method: 'POST',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to complete session');
  }

  return response.json();
}

/**
 * Cancel a session
 */
export async function cancelSession(sessionId: string): Promise<SessionResponse<void>> {
  const response = await fetch(getApiUrl(`/onboarding/v1/session/${sessionId}`), {
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to cancel session');
  }

  return response.json();
}

/**
 * Get candidate by Supabase user ID
 */
export async function getCandidateBySupabaseId(supabaseUserId: string): Promise<SessionResponse<CandidateData>> {
  const response = await fetch(getApiUrl(`/onboarding/v1/candidate/${supabaseUserId}`), {
    method: 'GET',
    headers: {
      ...getAuthHeaders(),
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to get candidate');
  }

  return response.json();
}

/**
 * Update candidate preferences
 */
export async function updateCandidatePreferences(
  supabaseUserId: string,
  preferences: any
): Promise<SessionResponse<void>> {
  const response = await fetch(getApiUrl(`/onboarding/v1/candidate/${supabaseUserId}/preferences`), {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(preferences),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to update preferences');
  }

  return response.json();
}

/**
 * Get user sessions
 */
export async function getUserSessions(
  userId?: string,
  email?: string,
  limit: number = 10
): Promise<SessionResponse<SessionData[]>> {
  const params = new URLSearchParams();
  if (userId) params.append('user_id', userId);
  if (email) params.append('email', email);
  params.append('limit', limit.toString());

  const response = await fetch(getApiUrl(`/onboarding/v1/session/user?${params.toString()}`), {
    method: 'GET',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to get user sessions');
  }

  return response.json();
}

/**
 * Onboarding Status Response
 */
export interface OnboardingStatus {
  resumeUploaded: boolean;
  resumeParsed: boolean;
  hasPreferences: boolean;
  hasMissingQuestions: boolean;
  pendingQuestionsCount: number;
  missingQuestions: MissingContentQuestion[];
  profileCompleted: boolean;
}

export interface MissingContentQuestion {
  questionId: number;
  question: string;
  status: 'pending' | 'answered' | 'skipped';
  answer?: string;
  answeredAt?: string;
  isRequired: boolean;
}

/**
 * Get onboarding status for a candidate
 */
export async function getOnboardingStatus(
  supabaseUserId: string
): Promise<SessionResponse<OnboardingStatus>> {
  const response = await fetch(getApiUrl(`/onboarding/v1/candidate/${supabaseUserId}/status`), {
    method: 'GET',
    headers: {
      ...getAuthHeaders(),
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to get onboarding status');
  }

  return response.json();
}

/**
 * Answer a missing content question
 */
export async function answerMissingContentQuestion(
  supabaseUserId: string,
  questionId: number,
  answer: string
): Promise<SessionResponse<{ allQuestionsCompleted: boolean; missingQuestions: MissingContentQuestion[] }>> {
  const response = await fetch(getApiUrl(`/onboarding/v1/candidate/${supabaseUserId}/question/answer`), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify({
      questionId: questionId,
      answer: answer,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to answer question');
  }

  return response.json();
}

/**
 * Skip a missing content question
 */
export async function skipMissingContentQuestion(
  supabaseUserId: string,
  questionId: number
): Promise<SessionResponse<{ allQuestionsCompleted: boolean; missingQuestions: MissingContentQuestion[] }>> {
  const response = await fetch(getApiUrl(`/onboarding/v1/candidate/${supabaseUserId}/question/skip`), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify({
      questionId: questionId,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to skip question');
  }

  return response.json();
}

/**
 * Send a message to the onboarding chat (LLM-powered)
 */
export async function sendOnboardingChatMessage(
  supabaseUserId: string,
  message: string,
  conversationHistory: string[],
  currentQuestionId?: number
): Promise<SessionResponse<{ response: string }>> {
  const response = await fetch(getApiUrl(`/onboarding/v1/candidate/${supabaseUserId}/chat`), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify({
      message: message,
      conversationHistory: conversationHistory,
      currentQuestionId: currentQuestionId,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to send chat message');
  }

  return response.json();
}

/**
 * Mark candidate profile as completed
 */
export async function markProfileCompleted(
  supabaseUserId: string
): Promise<SessionResponse<void>> {
  const response = await fetch(getApiUrl(`/onboarding/v1/candidate/${supabaseUserId}/complete`), {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to mark profile as completed');
  }

  return response.json();
}

/**
 * Update candidate preferences (career goals, job preferences, etc.)
 */
export async function updatePreferences(
  supabaseUserId: string,
  preferences: any
): Promise<SessionResponse<void>> {
  const response = await fetch(getApiUrl(`/onboarding/v1/candidate/${supabaseUserId}/preferences`), {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(preferences),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to update preferences');
  }

  return response.json();
}

// ========================================
// /me routes - Use JWT token for authentication
// These routes use the MongoDB ID from the JWT token
// ========================================

/**
 * Get current candidate (using JWT token)
 * This route extracts the MongoDB ID from the token
 */
export async function getCurrentCandidate(): Promise<SessionResponse<CandidateData>> {
  const response = await fetch(getApiUrl('/onboarding/v1/candidate/me'), {
    method: 'GET',
    headers: {
      ...getAuthHeaders(),
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to get candidate');
  }

  return response.json();
}

/**
 * Get onboarding status for current user (using JWT token)
 */
export async function getCurrentOnboardingStatus(): Promise<SessionResponse<OnboardingStatus>> {
  const response = await fetch(getApiUrl('/onboarding/v1/candidate/me/status'), {
    method: 'GET',
    headers: {
      ...getAuthHeaders(),
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to get onboarding status');
  }

  return response.json();
}

/**
 * Update preferences for current user (using JWT token)
 */
export async function updateCurrentPreferences(
  preferences: any
): Promise<SessionResponse<void>> {
  const response = await fetch(getApiUrl('/onboarding/v1/candidate/me/preferences'), {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(preferences),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to update preferences');
  }

  return response.json();
}

/**
 * Mark current user's profile as completed (using JWT token)
 */
export async function markCurrentProfileCompleted(): Promise<SessionResponse<void>> {
  const response = await fetch(getApiUrl('/onboarding/v1/candidate/me/complete'), {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to mark profile as completed');
  }

  return response.json();
}

/**
 * Answer a missing content question for current user (using JWT token)
 */
export async function answerCurrentMissingContentQuestion(
  questionId: number,
  answer: string
): Promise<SessionResponse<{ allQuestionsCompleted: boolean; missingQuestions: MissingContentQuestion[] }>> {
  const response = await fetch(getApiUrl('/onboarding/v1/candidate/me/question/answer'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify({
      questionId: questionId,
      answer: answer,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to answer question');
  }

  return response.json();
}

/**
 * Skip a missing content question for current user (using JWT token)
 */
export async function skipCurrentMissingContentQuestion(
  questionId: number
): Promise<SessionResponse<{ allQuestionsCompleted: boolean; missingQuestions: MissingContentQuestion[] }>> {
  const response = await fetch(getApiUrl('/onboarding/v1/candidate/me/question/skip'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify({
      questionId: questionId,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to skip question');
  }

  return response.json();
}

/**
 * Send a chat message for current user (using JWT token)
 */
export async function sendCurrentOnboardingChatMessage(
  message: string,
  conversationHistory: string[],
  currentQuestionId?: number
): Promise<SessionResponse<{ response: string }>> {
  const response = await fetch(getApiUrl('/onboarding/v1/candidate/me/chat'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify({
      message: message,
      conversationHistory: conversationHistory,
      currentQuestionId: currentQuestionId,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to send chat message');
  }

  return response.json();
}
